import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import { appToast } from '../../../components/AppToast'
import { executePrintReport } from '../../../utils/printReportUtil'
import { useExportJob } from '../../../hooks/useExportJob'
import { useStudentLandingListScreen } from './hooks/useStudentLandingListScreen'

const PAGE_SIZE = 25

const SEARCH_FIELD_OPTIONS = [
  { value: 'Page Name', label: 'Page Name' },
  { value: 'Company', label: 'Company' },
  { value: 'Branch', label: 'Branch' },
]

export default function StudentLandingListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useStudentLandingListScreen()
  const { triggerExport } = useExportJob({ module: 'student_landing' })
  const [page, setPage] = useState(1)

  const totalCount = state.rows.length
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const startIdx = (page - 1) * PAGE_SIZE
  const visibleRows = state.rows.slice(startIdx, startIdx + PAGE_SIZE)

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'print') {
      executePrintReport({
        title: 'Student Landing Pages Report',
        columns: [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Page Name' },
          { key: 'company', label: 'Company' },
          { key: 'branch', label: 'Branch' },
          { key: 'createdDate', label: 'Created Date' },
          { key: 'status', label: 'Status' },
        ],
        data: state.rows,
      })
    } else if (toolKey === 'export_excel') {
      triggerExport({
        searchField: state.searchField,
        searchText: state.searchText,
        company: state.companyFilter,
        branch: state.branchFilter,
      })
    }
  }

  const handleCopyUrl = async (url) => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      appToast.success('Landing page public link copied to clipboard!')
    } catch {
      appToast.error('Could not copy link.')
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">

      {/* ── Screen Header (Rule 87: Canopy Hierarchy - No local Add CTA button; use top navbar) ── */}
      <section className="flex flex-col gap-1 border-b border-outline-variant/10 pb-5">
        <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
          Landing Pages
        </h1>
        <p className="mt-1 text-xs font-medium text-on-surface-variant">
          Manage admission landing pages, default company and branch assignments, and public URL previews.
        </p>
      </section>

      {/* ── Error Banner ── */}
      {state.error && (
        <div className="rounded-2xl border border-error/30 bg-error-container px-4 py-3 text-xs font-medium text-error">
          {state.error}
        </div>
      )}

      {/* ── Shared Filters Canopy ── */}
      <SharedFilters
        searchField={state.searchField}
        setSearchField={actions.setSearchField}
        searchText={state.searchText}
        setSearchText={actions.setSearchText}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        companyFilter={state.companyFilter}
        setCompanyFilter={actions.setCompanyFilter}
        branchFilter={state.branchFilter}
        setBranchFilter={actions.setBranchFilter}
        visibleAccordionFilters={['company', 'branch']}
        tools={['print', 'export_excel']}
        onToolSelect={handleToolSelect}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
      />

      {/* ── Semantic Data Table (Standard 5.C) ── */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                <th className="w-24 px-5 py-3.5 text-center">Action</th>
                <th className="px-5 py-3.5">Page Name</th>
                <th className="px-5 py-3.5">Company</th>
                <th className="px-5 py-3.5">Branch</th>
                <th className="px-5 py-3.5">Created Date</th>
                <th className="w-28 px-5 py-3.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10 text-on-surface">
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
                    Loading landing pages...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <span className="material-symbols-outlined mb-2 block text-5xl text-on-surface-variant/30">
                      inbox
                    </span>
                    <p className="text-sm font-semibold text-on-surface">No landing pages match current filters</p>
                    <p className="text-xs text-on-surface-variant mt-1">
                      Try adjusting the search criteria or click Reset to restore defaults.
                    </p>
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/admission/student-landing/entry?id=${row.id}`)}
                    className="cursor-pointer transition-colors hover:bg-surface-container-low/50"
                  >
                    {/* Action Column */}
                    <td
                      className="px-5 py-3 text-center select-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                          items={[
                            { key: 'view', label: 'View', icon: 'visibility' },
                            { key: 'edit', label: 'Edit', icon: 'edit' },
                            { key: 'copy_url', label: 'Copy Public URL', icon: 'content_copy' },
                            { key: 'open_url', label: 'Open Public Page', icon: 'open_in_new' },
                            { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                          ]}
                          onSelect={(key) => {
                            if (key === 'view') {
                              navigate(`/admission/student-landing/entry?id=${row.id}&view=1`)
                            } else if (key === 'edit') {
                              navigate(`/admission/student-landing/entry?id=${row.id}`)
                            } else if (key === 'copy_url') {
                              handleCopyUrl(row.url)
                            } else if (key === 'open_url') {
                              window.open(row.url || '#', '_blank', 'noopener,noreferrer')
                            } else if (key === 'delete') {
                              actions.handleDelete(row.id)
                            }
                          }}
                        />
                      </div>
                    </td>

                    {/* Page Name */}
                    <td className="px-5 py-3 font-semibold text-on-surface">
                      <span className="hover:text-primary transition-colors">{row.name}</span>
                    </td>

                    {/* Company */}
                    <td className="px-5 py-3 text-on-surface-variant">
                      {row.company || '—'}
                    </td>

                    {/* Branch */}
                    <td className="px-5 py-3 text-on-surface-variant">
                      {row.branch || '—'}
                    </td>

                    {/* Created Date */}
                    <td className="px-5 py-3 text-on-surface-variant font-mono text-[11px]">
                      {row.createdDate || '—'}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                          row.status === 'Active'
                            ? 'bg-emerald-500/10 text-emerald-600'
                            : 'bg-slate-500/10 text-slate-500'
                        }`}
                      >
                        {row.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Standard Pagination Footer (Standard 5.E) ── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {totalCount === 0 ? 0 : startIdx + 1} to{' '}
            {Math.min(startIdx + PAGE_SIZE, totalCount)} of {totalCount} entries
          </p>
          <AppPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onPrevious={() => setPage((p) => Math.max(p - 1, 1))}
            onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
            canPrevious={page > 1}
            canNext={page < totalPages}
          />
        </div>
      </div>
    </div>
  )
}
